import { useQuery } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router";
import { ArrowRight, CheckCircle2, Circle, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { StatusBadge } from "@/components/shared/status-badge";
import { useGetApiAgents } from "@/lib/api/generated/agents/agents";
import { getApiCapabilityBindings, getApiCapabilityTargets } from "@/lib/api/generated/capabilities/capabilities";
import { useGetApiChannelConnections } from "@/lib/api/generated/channels/channels";
import { useGetApiChannelDeployments } from "@/lib/api/generated/channels/channels";
import { useGetApiConnectorInstalls } from "@/lib/api/generated/channels/channels";
import { useGetApiLightweightAutomations } from "@/lib/api/generated/automations/automations";
import { useGetApiLiveSnapshot } from "@/lib/api/generated/state/state";
import { useGetApiRuntimeTarget } from "@/lib/api/generated/runtime-targets/runtime-targets";
import { getApiState } from "@/lib/api/generated/state/state";
import { useGetApiTenantsTenantId } from "@/lib/api/generated/tenants/tenants";
import { useGetApiWorkflowPolicies } from "@/lib/api/generated/workflows/workflows";
import { postApiExecutions } from "@/lib/api/generated/workflows/workflows";
import { formatRelativeTime, truncateId } from "@/lib/utils/formatting";
import { CardSkeleton } from "@/components/shared/loading-skeleton";
import { useDocumentTitle } from "@/lib/hooks/use-document-title";
import { useAuth } from "@/features/auth/auth-provider";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import { queryKeys } from "@/lib/query/keys";
import { ROUTES } from "@/app/routes";
import {
  isTenantIdentifierLike,
  resolveWorkspaceDisplayName,
} from "@/lib/tenant-display";
import type { TenantLiveSnapshotRecord } from "@/lib/types/live-snapshot";

function StatCard({ label, value, href }: { label: string; value: number | string; href?: string }) {
  const card = (
    <Card>
      <CardHeader>
        <CardTitle>{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold" style={{ color: "var(--text-strong)" }}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
  if (href) {
    return <Link to={href} className="cursor-pointer">{card}</Link>;
  }
  return card;
}

function SummaryCard({ title, count, href, description }: { title: string; count: number; href: string; description?: string }) {
  return (
    <Link to={href} className="no-underline">
      <Card className="hover:border-(--border-hover) transition-colors cursor-pointer">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
        <CardContent>
          <div className="text-xl font-bold" style={{ color: "var(--text-strong)" }}>
            {count}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const { tenant, isPlatformAdmin } = useAuth();
  const { tenantId: activeTenantId } = useTenantContext();

  const { data: tenantData, isLoading: isLoadingTenant } = useGetApiTenantsTenantId(activeTenantId);
  const { data: summaries, isLoading: isLoadingSummary } = useQuery({
    queryKey: ["state-summaries", activeTenantId],
    queryFn: () => getApiState({ tenantId: activeTenantId }),
    enabled: Boolean(activeTenantId),
  });
  const { data: executions = [], isLoading: isLoadingExecutions } = useQuery({
    queryKey: ["executions", activeTenantId],
    queryFn: () => postApiExecutions({ tenantId: activeTenantId }),
    enabled: Boolean(activeTenantId),
  });
  const tenantScopedQueryOptions = { query: { enabled: Boolean(activeTenantId) } };
  const runtimeTargetQueryOptions = {
    query: {
      enabled: Boolean(activeTenantId),
      queryKey: queryKeys.tenantRuntimeTarget(activeTenantId ?? "none"),
    },
  };
  const liveSnapshotQueryOptions = {
    query: {
      enabled: Boolean(activeTenantId),
      queryKey: queryKeys.liveSnapshot(activeTenantId ?? "none"),
    },
  };
  const { data: agents } = useGetApiAgents(tenantScopedQueryOptions);
  const { data: automations = [] } = useGetApiLightweightAutomations(tenantScopedQueryOptions);
  const { data: runtimeTarget } = useGetApiRuntimeTarget(runtimeTargetQueryOptions);
  const { data: connections } = useGetApiChannelConnections(tenantScopedQueryOptions);
  const { data: deployments } = useGetApiChannelDeployments(tenantScopedQueryOptions);
  const { data: connectors } = useGetApiConnectorInstalls(tenantScopedQueryOptions);
  const { data: capabilityBindings = [] } = useQuery({
    queryKey: ["capability-bindings", activeTenantId],
    queryFn: () => getApiCapabilityBindings({ tenantId: activeTenantId }),
    enabled: Boolean(activeTenantId),
  });
  const { data: capabilityTargets = [] } = useQuery({
    queryKey: ["capability-targets", activeTenantId],
    queryFn: () => getApiCapabilityTargets({ tenantId: activeTenantId }),
    enabled: Boolean(activeTenantId),
  });
  const { data: policies } = useGetApiWorkflowPolicies(tenantScopedQueryOptions);
  const { data: liveSnapshotData } = useGetApiLiveSnapshot(liveSnapshotQueryOptions);
  const liveSnapshot = liveSnapshotData as unknown as TenantLiveSnapshotRecord | null | undefined;

  const workspaceName = resolveWorkspaceDisplayName(
    activeTenantId,
    [
      tenantData?.name,
      tenantData?.authTenantName,
      tenant?.displayName,
      tenantData?.slug,
      tenantData?.authTenantSlug,
      tenant?.tenantSlug,
    ],
    "Workspace",
  );
  const workspaceSecondaryLabel = tenantData?.slug ?? tenant?.tenantSlug ?? null;

  useDocumentTitle(`${workspaceName} — Dashboard`);

  const isLoading = isLoadingSummary || isLoadingExecutions || isLoadingTenant;
  const summary = summaries?.[0];

  const agentCount = agents?.length ?? 0;
  const automationCount = automations?.length ?? 0;
  const connectionCount = connections?.length ?? 0;
  const deploymentCount = deployments?.length ?? 0;
  const connectorCount = connectors?.length ?? 0;
  const integrationCount = connectorCount + connectionCount;
  const policyCount = policies?.length ?? 0;

  const workspaceStatus = tenantData?.workspaceStatus ?? (runtimeTarget ? "ready" : undefined);
  const workspaceStatusMessage = getWorkspaceStatusMessage(workspaceStatus, tenantData?.workspaceStatusMessage, isPlatformAdmin);

  const setupSteps = [
    {
      title: "Connect your first integration",
      href: ROUTES.tenantPlugins(activeTenantId),
      estimate: "3 min",
      complete: integrationCount > 0,
      description: "Connect the systems this workspace needs so agents can read context and send results somewhere real.",
      statusNote:
        integrationCount > 0
          ? `${integrationCount} integration${integrationCount === 1 ? "" : "s"} connected`
          : "Start here if you want real data instead of a blank setup.",
    },
    {
      title: "Create your first agent",
      href: ROUTES.tenantAgentNew(activeTenantId),
      estimate: "4 min",
      complete: agentCount > 0,
      description: "Give the assistant a role, instructions, and the behavior you want it to follow.",
      statusNote:
        agentCount > 0
          ? `${agentCount} agent${agentCount === 1 ? "" : "s"} ready`
          : "Automations run through an agent, so define this before building flows.",
    },
    {
      title: "Create your first automation",
      href: agentCount > 0 ? ROUTES.tenantAutomationNew(activeTenantId, "research_news_digest") : ROUTES.tenantAgentNew(activeTenantId),
      estimate: "5 min",
      complete: automationCount > 0,
      description: "Start from a template, define the task in plain language, then run it once before adding a schedule.",
      statusNote:
        automationCount > 0
          ? `${automationCount} automation${automationCount === 1 ? "" : "s"} created`
          : agentCount > 0
            ? "Templates are available once you open the automation editor."
            : "Create an agent first, then pick a starter automation.",
    },
  ] as const;
  const remainingSetupSteps = setupSteps.filter((step) => !step.complete);
  const nextSetupStep = remainingSetupSteps[0] ?? null;
  const showGettingStarted = remainingSetupSteps.length > 0;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <h1 className="text-lg font-semibold" style={{ color: "var(--text-strong)" }}>
          Dashboard
        </h1>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="flex flex-col gap-3">
        <h1 className="text-lg font-semibold" style={{ color: "var(--text-strong)" }}>
          Dashboard
        </h1>
        <p className="text-sm" style={{ color: "var(--muted-foreground)" }}>
          This application requires a tenant-bound token.
        </p>
      </div>
    );
  }

  const failedExecutions = executions.filter((execution) => execution.status === "failed");
  const recentExecutions = [...executions]
    .toSorted((a, b) => b.startedAt.localeCompare(a.startedAt))
    .slice(0, 8);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold" style={{ color: "var(--text-strong)" }}>
            Dashboard
          </h1>
          <p className="text-sm mt-1" style={{ color: "var(--muted-foreground)" }}>
            Operating inside <span style={{ color: "var(--text-strong)" }}>{workspaceName}</span>.
          </p>
        </div>
        {tenantData && (
          <div className="flex items-center gap-2">
            {tenantData.status && <StatusBadge status={tenantData.status} />}
            {workspaceSecondaryLabel &&
            !isTenantIdentifierLike(workspaceSecondaryLabel) &&
            workspaceSecondaryLabel !== workspaceName && (
              <span className="text-xs font-mono" style={{ color: "var(--muted-foreground)" }}>
                {workspaceSecondaryLabel}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Getting started */}
      {showGettingStarted && (
        <>
          <Card>
            <CardHeader className="gap-3">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="space-y-2">
                  <CardTitle>Getting started</CardTitle>
                  <CardDescription>
                    New tenants should not start with raw infrastructure. Use these steps to get to a first real outcome.
                  </CardDescription>
                </div>
                {nextSetupStep ? (
                  <Button asChild size="sm">
                    <Link to={nextSetupStep.href}>
                      {nextSetupStep.title}
                      <ArrowRight size={14} />
                    </Link>
                  </Button>
                ) : null}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                {setupSteps.map((step) => (
                  <SetupStepCard
                    key={step.title}
                    href={step.href}
                    title={step.title}
                    estimate={step.estimate}
                    complete={step.complete}
                    description={step.description}
                    statusNote={step.statusNote}
                  />
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Starter paths</CardTitle>
              <CardDescription>
                Pick a concrete first proof instead of starting from a blank editor.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 md:grid-cols-3">
                <StarterPathCard
                  title="News scan"
                  description="Track market or industry changes and deliver a recurring digest."
                  href={agentCount > 0 ? ROUTES.tenantAutomationNew(activeTenantId, "research_news_digest") : ROUTES.tenantAgentNew(activeTenantId)}
                  actionLabel={agentCount > 0 ? "Use template" : "Create agent first"}
                />
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Workspace status */}
      {workspaceStatus && workspaceStatusMessage ? (
        <WorkspaceStatusCard
          status={workspaceStatus}
          message={workspaceStatusMessage}
          isPlatformAdmin={isPlatformAdmin}
        />
      ) : null}

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Agents" value={summary?.agentCount ?? 0} href={ROUTES.tenantAgents(activeTenantId)} />
        <StatCard label="Channels" value={summary?.channelConnectionCount ?? 0} href={ROUTES.tenantChannels(activeTenantId)} />
        <StatCard label="Automations" value={automationCount} href={ROUTES.tenantAutomations(activeTenantId)} />
        <StatCard label="Failed Executions" value={failedExecutions.length} href="/executions?status=failed" />
      </div>

      {/* Tenant info */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Tenant information</CardTitle>
          <Button variant="outline" size="sm" onClick={() => navigate("/workspace/edit")}>
            <Pencil size={14} />
            Edit
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-xs">
            <div>
              <span style={{ color: "var(--muted-foreground)" }}>ID</span>
              <p className="font-mono" style={{ color: "var(--text-strong)" }}>{tenantData?.tenantId}</p>
            </div>
            <div>
              <span style={{ color: "var(--muted-foreground)" }}>Timezone</span>
              <p style={{ color: "var(--text-strong)" }}>{tenantData?.timezone ?? "—"}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent executions */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Executions</CardTitle>
        </CardHeader>
        <CardContent>
          {recentExecutions.length === 0 ? (
            <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
              No executions yet.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {recentExecutions.map((exec) => (
                <div
                  key={exec.executionId}
                  className="flex items-center justify-between py-1.5 border-b border-border last:border-0 cursor-pointer"
                  onClick={() => navigate(`/executions/${exec.executionId}`)}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-mono" style={{ color: "var(--muted-foreground)" }}>
                      {truncateId(exec.executionId)}
                    </span>
                    <span className="text-xs">{exec.workflowKey}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge status={exec.status} />
                    <span className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                      {formatRelativeTime(exec.startedAt)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent failures */}
      {failedExecutions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Recent Failures</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-2">
              {[...failedExecutions]
                .toSorted((a, b) => b.startedAt.localeCompare(a.startedAt))
                .slice(0, 5)
                .map((exec) => (
                <div
                  key={exec.executionId}
                  className="flex items-center justify-between py-1.5 border-b border-border last:border-0 cursor-pointer"
                    onClick={() => navigate(`/executions/${exec.executionId}`)}
                  >
                    <span className="text-xs">{exec.workflowKey}</span>
                    <span className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                      {formatRelativeTime(exec.startedAt)}
                    </span>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary cards (post-setup) */}
      {!showGettingStarted && isPlatformAdmin ? (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Connector journey</CardTitle>
              <CardDescription>
                Install a connector, activate a business capability, and point it at a named target.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 md:grid-cols-3">
                <JourneyStepCard
                  href={ROUTES.tenantConnectors(activeTenantId)}
                  label="1. Connector Installs"
                  value={String(connectorCount)}
                  description="Installed connectors ready to be mapped."
                />
                <JourneyStepCard
                  href={ROUTES.tenantImplementations(activeTenantId)}
                  label="2. Connector setup"
                  value={String(capabilityBindings?.length ?? 0)}
                  description="Installed connectors wired to the capabilities they power."
                />
                <JourneyStepCard
                  href={ROUTES.tenantImplementations(activeTenantId)}
                  label="3. Targets"
                  value={String(capabilityTargets?.length ?? 0)}
                  description="Named destinations like queues, inboxes, or accounts."
                />
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
            <SummaryCard title="Agents" count={agentCount} href={ROUTES.tenantAgents(activeTenantId)} />
            <SummaryCard title="Channel connections" count={connectionCount} href={ROUTES.tenantChannels(activeTenantId)} />
            <SummaryCard title="Deployments" count={deploymentCount} href={ROUTES.tenantDeployments(activeTenantId)} />
            <SummaryCard
              title="Connector installs"
              count={connectorCount}
              href={ROUTES.tenantConnectors(activeTenantId)}
              description="Install the real connectors this tenant relies on."
            />
            <SummaryCard
              title="Connector setup"
              count={capabilityBindings?.length ?? 0}
              href={ROUTES.tenantImplementations(activeTenantId)}
              description="Choose which connector powers each runtime capability."
            />
            <SummaryCard
              title="Named targets"
              count={capabilityTargets?.length ?? 0}
              href={ROUTES.tenantImplementations(activeTenantId)}
              description="Route one activation to the right queue, inbox, or account."
            />
            <SummaryCard
              title="Automations"
              count={automationCount}
              href={ROUTES.tenantAutomations(activeTenantId)}
              description="Prompt-first scheduled or manual automations bound to tenant agents."
            />
            <SummaryCard
              title="Governance policies"
              count={policyCount}
              href={ROUTES.tenantWorkflows(activeTenantId)}
              description="Governance rules for built-in workflows and channel policy."
            />
            <SummaryCard title="Snapshots" count={liveSnapshot ? 1 : 0} href={ROUTES.tenantSnapshots(activeTenantId)} />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Workspace runtime</CardTitle>
              <CardDescription>
                This only matters when you are deploying, running, or troubleshooting tenant work.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {runtimeTarget ? (
                <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-xs">
                  <div>
                    <span style={{ color: "var(--muted-foreground)" }}>Runtime key</span>
                    <p className="font-mono" style={{ color: "var(--text-strong)" }}>{runtimeTarget.runtimeKey}</p>
                  </div>
                  <div>
                    <span style={{ color: "var(--muted-foreground)" }}>Base URL</span>
                    <p className="font-mono" style={{ color: "var(--text-strong)" }}>{runtimeTarget.baseUrl}</p>
                  </div>
                </div>
              ) : (
                <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                  No runtime target configured.{" "}
                  <Link to={ROUTES.tenantRuntime(activeTenantId)} className="text-accent">
                    Configure now
                  </Link>
                </p>
              )}
            </CardContent>
          </Card>

          {liveSnapshot && (
            <Card>
              <CardHeader>
                <CardTitle>Live snapshot</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-xs">
                  <div>
                    <span style={{ color: "var(--muted-foreground)" }}>Generation</span>
                    <p className="font-mono" style={{ color: "var(--text-strong)" }}>
                      {liveSnapshot.snapshot.generation}
                    </p>
                  </div>
                  <div>
                    <span style={{ color: "var(--muted-foreground)" }}>Hash</span>
                    <p className="font-mono" style={{ color: "var(--text-strong)" }}>
                      {liveSnapshot.snapshot.snapshotHash}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Governance & Advanced</CardTitle>
              <CardDescription>
                Lower-frequency admin and governance screens. Keep daily work in Agents, Automations,
                Integrations, Channels, and Users.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                <JourneyStepCard
                  href={ROUTES.tenantConnectors(activeTenantId)}
                  label="Connector installs"
                  value={String(connectorCount)}
                  description="Connector installations and credential plumbing."
                />
                <JourneyStepCard
                  href={ROUTES.tenantImplementations(activeTenantId)}
                  label="Capability mappings"
                  value={String(capabilityBindings?.length ?? 0)}
                  description="Map business capabilities to connector installs and named targets."
                />
                <JourneyStepCard
                  href={ROUTES.tenantWorkflows(activeTenantId)}
                  label="Governance policies"
                  value={String(policyCount)}
                  description="Governance rules for built-in workflows and channel policy."
                />
                <JourneyStepCard
                  href={ROUTES.tenantSnapshots(activeTenantId)}
                  label="Snapshots"
                  value={liveSnapshot ? "1" : "0"}
                  description="Inspect the currently applied runtime snapshot state."
                />
              </div>
            </CardContent>
          </Card>
        </>
      ) : null}

      {!showGettingStarted && !isPlatformAdmin ? (
        <>
          <div className="grid gap-4 md:grid-cols-3">
            <SummaryCard
              title="Integrations"
              count={integrationCount}
              href={ROUTES.tenantPlugins(activeTenantId)}
              description="Systems this workspace can read from or write to."
            />
            <SummaryCard
              title="Agents"
              count={agentCount}
              href={ROUTES.tenantAgents(activeTenantId)}
              description="Assistants ready to take on roles and instructions."
            />
            <SummaryCard
              title="Automations"
              count={automationCount}
              href={ROUTES.tenantAutomations(activeTenantId)}
              description="Repeatable tasks and scheduled flows running for this tenant."
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Daily focus</CardTitle>
              <CardDescription>
                Keep tenant work centered on outcomes, not platform plumbing.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <JourneyStepCard
                  href={ROUTES.tenantPlugins(activeTenantId)}
                  label="Integrations"
                  value={String(integrationCount)}
                  description="Connect the systems this tenant depends on."
                />
                <JourneyStepCard
                  href={ROUTES.tenantAgents(activeTenantId)}
                  label="Agents"
                  value={String(agentCount)}
                  description="Shape the assistant roles and instructions behind each flow."
                />
                <JourneyStepCard
                  href={ROUTES.tenantAutomations(activeTenantId)}
                  label="Automations"
                  value={String(automationCount)}
                  description="Start from templates and build the repeatable work this tenant needs."
                />
                <JourneyStepCard
                  href={ROUTES.tenantActivity(activeTenantId)}
                  label="Activity"
                  value="View"
                  description="See recent runs, status changes, and the latest tenant activity."
                />
              </div>
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}

function WorkspaceStatusCard({
  status,
  message,
  isPlatformAdmin,
}: {
  status: "preparing" | "ready" | "needs_attention";
  message: string;
  isPlatformAdmin: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="space-y-1">
            <CardTitle>{isPlatformAdmin ? "Platform status" : "Workspace status"}</CardTitle>
            <CardDescription>
              {isPlatformAdmin
                ? "Managed infrastructure stays off the tenant surface unless you need to inspect it."
                : "The platform team manages the workspace so tenant setup can stay focused on outcomes."}
            </CardDescription>
          </div>
          <StatusBadge status={status} />
        </div>
      </CardHeader>
      <CardContent className="text-sm leading-6" style={{ color: "var(--muted-foreground)" }}>
        {message}
      </CardContent>
    </Card>
  );
}

function SetupStepCard({
  href,
  title,
  estimate,
  complete,
  description,
  statusNote,
}: {
  href: string;
  title: string;
  estimate: string;
  complete: boolean;
  description: string;
  statusNote: string;
}) {
  const StatusIcon = complete ? CheckCircle2 : Circle;

  return (
    <Link to={href} className="no-underline">
      <div
        className="flex h-full flex-col gap-3 rounded-md border px-4 py-4 transition-colors hover:border-(--border-hover)"
        style={{ borderColor: "var(--border)", background: "var(--panel)" }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <StatusIcon
              size={18}
              style={{ color: complete ? "var(--accent)" : "var(--muted-foreground)" }}
            />
            <div>
              <div className="text-sm font-semibold" style={{ color: "var(--text-strong)" }}>
                {title}
              </div>
              <div className="mt-1 text-xs" style={{ color: "var(--muted-foreground)" }}>
                {description}
              </div>
            </div>
          </div>
          <span className="text-[11px] uppercase tracking-[0.14em]" style={{ color: "var(--muted-foreground)" }}>
            {estimate}
          </span>
        </div>
        <div className="mt-auto text-xs font-medium" style={{ color: complete ? "var(--accent)" : "var(--muted-foreground)" }}>
          {statusNote}
        </div>
      </div>
    </Link>
  );
}

function StarterPathCard({
  title,
  description,
  href,
  actionLabel,
}: {
  title: string;
  description: string;
  href: string;
  actionLabel: string;
}) {
  return (
    <Link to={href} className="no-underline">
      <div
        className="flex h-full flex-col gap-4 rounded-md border px-4 py-4 transition-colors hover:border-(--border-hover)"
        style={{ borderColor: "var(--border)", background: "var(--panel)" }}
      >
        <div>
          <div className="text-sm font-semibold" style={{ color: "var(--text-strong)" }}>
            {title}
          </div>
          <p className="mt-1 text-xs leading-5" style={{ color: "var(--muted-foreground)" }}>
            {description}
          </p>
        </div>
        <div className="text-[11px] font-semibold uppercase tracking-[0.14em]" style={{ color: "var(--accent)" }}>
          {actionLabel}
        </div>
      </div>
    </Link>
  );
}

function JourneyStepCard({
  href,
  label,
  value,
  description,
}: {
  href: string;
  label: string;
  value: string;
  description: string;
}) {
  return (
    <Link to={href} className="no-underline">
      <div
        className="rounded-md border px-4 py-4 transition-colors hover:border-(--border-hover)"
        style={{ borderColor: "var(--border)", background: "var(--panel)" }}
      >
        <div className="text-[11px] font-semibold uppercase tracking-[0.14em]" style={{ color: "var(--muted-foreground)" }}>
          {label}
        </div>
        <div className="mt-2 text-2xl font-semibold" style={{ color: "var(--text-strong)" }}>
          {value}
        </div>
        <p className="mt-1 text-xs" style={{ color: "var(--muted-foreground)" }}>
          {description}
        </p>
      </div>
    </Link>
  );
}

function getWorkspaceStatusMessage(
  status: "preparing" | "ready" | "needs_attention" | undefined,
  explicitMessage: string | undefined,
  isPlatformAdmin: boolean,
): string | null {
  if (explicitMessage?.trim()) {
    return explicitMessage;
  }

  if (status === "preparing") {
    return isPlatformAdmin
      ? "The platform is preparing this workspace in the background. Tenant setup can continue now."
      : "We're preparing your workspace in the background. You can keep configuring integrations, agents, and automations now.";
  }
  if (status === "ready") {
    return isPlatformAdmin
      ? "Managed runtime is ready. Keep day-to-day work focused on tenant outcomes, not infrastructure."
      : "Your workspace is ready. Focus on integrations, agents, automations, and results.";
  }
  if (status === "needs_attention") {
    return isPlatformAdmin
      ? "Workspace setup needs platform attention before end-to-end tenant work can run."
      : "The platform team needs to finish workspace setup before everything can run end to end.";
  }

  return null;
}
